// ── PROPERTY AND PROCUREMENT ─────────────────────────────────
// Two tabs: the equipment inventory (2 CFR 200.313(d)) with its two-yearly
// physical inventory and disposition rule, and the procurement file for each
// purchase with its checklist (2 CFR 200.318 to 200.325, Appendix II, 2 CFR 180).
// Every date, flag, checklist item and percentage is worked out on the
// server (lib/property.js).
import { useState, useEffect, useCallback } from "react";
import { TOKENS } from "../tokens";
import { FONT_H, FONT_UI, FONT_MONO, useT } from "../theme";
import { API, apiFetch } from "../utils/api";

// Same keys and labels as lib/property.js METHODS.
const METHODS = [
  ["micro", "Micro-purchase"], ["small", "Small purchase (simplified acquisition)"],
  ["sealed", "Sealed bids"], ["proposals", "Proposals"], ["noncompetitive", "Noncompetitive"],
];
const RED = "#dc2626", GREEN = "#059669", AMBER = "#d97706";

const newId = () => Math.random().toString(36).slice(2, 12);
const blankItem = () => ({
  id: newId(), description: "", idNumber: "", fundingSource: "", fain: "", awardId: "", titleHolder: "",
  acquisitionDate: "", cost: "", federalPct: 100, usefulLifeOverOneYear: true, location: "", use: "", condition: "",
  lastInventory: "", disposal: { date: "", salePrice: "", fairMarketValue: "" },
});
const blankBuy = () => ({ id: newId(), item: "", vendor: "", amount: "", date: "", awardId: "", awardLabel: "", method: "", note: "", checklist: {} });
const usd = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const awardName = (a) => [a.awardNumber || a.fain, a.funderName || a.projectTitle].filter(Boolean).join(" ") || a.id;

function saveFile(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export default function PropertyPage() {
  const T = useT();
  const [tab, setTab]       = useState("equipment");
  const [awards, setAwards] = useState([]);
  const [error, setError]   = useState("");

  useEffect(() => {
    apiFetch(`${API}/setup/awards`).then((r) => (r.ok ? r.json() : null)).then((d) => d && setAwards(d.records || [])).catch(() => {});
  }, []);

  const card  = { background: T.bgSurface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 20, marginBottom: 18 };
  const label = { fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: "0.1em", textTransform: "uppercase", color: T.textMuted, marginBottom: 6, display: "block" };
  const input = { width: "100%", padding: "9px 11px", background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: 8, color: T.textBody, fontSize: 13, fontFamily: FONT_UI, outline: "none", boxSizing: "border-box" };
  const btn = (primary) => ({ padding: "10px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600, fontFamily: FONT_UI, cursor: "pointer", minHeight: 38, border: `1px solid ${primary ? TOKENS.gold : T.border}`, background: primary ? TOKENS.gold : "transparent", color: primary ? "#fff" : T.textBody });
  const ui = { T, card, label, input, btn, awards, setError };

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "22px 24px 60px", background: T.bgApp }}>
      <div style={{ maxWidth: 1080, margin: "0 auto" }}>
        <h1 style={{ fontFamily: FONT_H, fontSize: 24, fontWeight: 700, color: T.text, margin: "0 0 4px" }}>Property and Procurement</h1>
        <p style={{ fontFamily: FONT_UI, fontSize: 13.5, color: T.textMuted, margin: "0 0 16px" }}>
          Keep the equipment records and the two-yearly physical inventory, and check that every purchase file holds what the auditor will ask for.
        </p>
        <div role="tablist" style={{ display: "flex", gap: 8, marginBottom: 18 }}>
          {[["equipment", "Equipment"], ["procurement", "Procurement"]].map(([k, v]) => (
            <button key={k} role="tab" aria-selected={tab === k} style={btn(tab === k)} onClick={() => { setTab(k); setError(""); }}>{v}</button>
          ))}
        </div>
        {error && <div role="alert" style={{ ...card, borderColor: RED, color: RED, fontFamily: FONT_UI, fontSize: 13 }}>{error}</div>}
        {tab === "equipment" ? <Records key="e" {...ui} kind="equipment" /> : <Records key="p" {...ui} kind="procurements" />}
      </div>
    </div>
  );
}

// List, editor and export for one kind. The two kinds share the store logic.
function Records(props) {
  const { T, card, btn, kind, setError } = props;
  const [records, setRecords] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy]       = useState(false);
  const isEquipment = kind === "equipment";

  const load = useCallback(async () => {
    try {
      const res = await apiFetch(`${API}/${kind}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Could not load the records.");
      setRecords(data.records || []);
    } catch (e) { setError(e.message); }
  }, [kind, setError]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn) => {
    setBusy(true); setError("");
    try { await fn(); } catch (e) { setError(e.message); }
    setBusy(false);
  };
  const save = () => run(async () => {
    const { check, updatedAt, ...body } = editing;
    const res = await apiFetch(`${API}/${kind}/${encodeURIComponent(editing.id)}`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Could not save.");
    setEditing(null);
    await load();
  });
  const remove = () => {
    if (!window.confirm("Delete this record? This cannot be undone.")) return;
    run(async () => {
      const res = await apiFetch(`${API}/${kind}/${encodeURIComponent(editing.id)}`, { method: "DELETE" });
      if (!res.ok) throw new Error((await res.json()).error || "Could not delete.");
      setEditing(null);
      await load();
    });
  };
  const download = (path, name) => run(async () => {
    const res = await apiFetch(`${API}/${path}`);
    if (!res.ok) throw new Error((await res.json()).error || "Could not build the file.");
    saveFile(await res.blob(), name);
  });

  const isNew = editing && !records.some((x) => x.id === editing.id);
  if (editing) {
    const Editor = isEquipment ? ItemEditor : BuyEditor;
    return (
      <div style={card}>
        <Editor {...props} r={editing} setR={setEditing} isNew={isNew} />
        <div style={{ display: "flex", gap: 10, marginTop: 18, flexWrap: "wrap" }}>
          <button style={btn(true)} onClick={save} disabled={busy}>{busy ? "Saving…" : "Save"}</button>
          <button style={btn(false)} onClick={() => setEditing(null)} disabled={busy}>Cancel</button>
          {!isEquipment && !isNew && <button style={btn(false)} onClick={() => download(`procurements/${encodeURIComponent(editing.id)}/checklist`, `procurement-file-${editing.id}.xlsx`)} disabled={busy}>Download checklist (saved version)</button>}
          {!isNew && <button style={{ ...btn(false), color: RED, marginLeft: "auto" }} onClick={remove} disabled={busy}>Delete</button>}
        </div>
      </div>
    );
  }
  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
        {isEquipment
          ? <button style={btn(false)} onClick={() => download("equipment/inventory", "equipment-inventory.xlsx")} disabled={busy}>Download inventory (Excel)</button>
          : <button style={btn(false)} onClick={() => download("procurements/register", "procurement-register.xlsx")} disabled={busy}>Download register (Excel)</button>}
        <button style={btn(true)} onClick={() => setEditing(isEquipment ? blankItem() : blankBuy())}>{isEquipment ? "New item" : "New purchase"}</button>
      </div>
      <List T={T} card={card} records={records} onOpen={setEditing} isEquipment={isEquipment} />
    </>
  );
}

function List({ T, card, records, onOpen, isEquipment }) {
  if (!records.length) return <div style={{ ...card, fontFamily: FONT_UI, fontSize: 13.5, color: T.textMuted }}>{isEquipment ? "No items yet. Add each piece of equipment bought with federal funds." : "No purchases yet. Add each purchase charged to an award."}</div>;
  const th = { textAlign: "left", padding: "8px 10px", fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase", color: T.textMuted, borderBottom: `1px solid ${T.border}` };
  const td = { padding: "10px", fontFamily: FONT_UI, fontSize: 13, color: T.textBody, borderBottom: `1px solid ${T.border}`, verticalAlign: "top" };
  const heads = isEquipment ? ["Item", "Cost", "Equipment", "Location", "Next inventory"] : ["Purchase", "Amount", "Method", "File", "Date"];
  return (
    <div style={{ ...card, padding: 0, overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr>{heads.map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
        <tbody>
          {records.map((r) => {
            const c = r.check || {};
            return (
              <tr key={r.id} onClick={() => onOpen(r)} onKeyDown={(e) => e.key === "Enter" && onOpen(r)} tabIndex={0} style={{ cursor: "pointer", opacity: c.disposed ? 0.55 : 1 }}>
                {isEquipment ? <>
                  <td style={td}><strong>{r.description}</strong><div style={{ color: T.textMuted, fontSize: 12 }}>{r.idNumber}</div></td>
                  <td style={td}>{usd(r.cost)}</td>
                  <td style={td}>{c.isEquipment ? "Yes" : "No"}</td>
                  <td style={td}>{r.location}</td>
                  <td style={td}>
                    {c.disposed ? `Disposed ${r.disposal?.date}` : c.nextInventory || "Not set"}
                    {c.inventoryOverdue && <span style={{ color: RED, marginLeft: 6 }}>overdue</span>}
                    {c.dispositionFlag && <div style={{ color: AMBER, fontSize: 12 }}>Federal share {usd(c.federalShare)} on disposal</div>}
                  </td>
                </> : <>
                  <td style={td}><strong>{r.item}</strong><div style={{ color: T.textMuted, fontSize: 12 }}>{r.vendor}{r.awardLabel ? ` · ${r.awardLabel}` : ""}</div></td>
                  <td style={td}>{usd(r.amount)}</td>
                  <td style={td}>{(METHODS.find(([k]) => k === r.method) || ["", ""])[1]}{c.flag && <div style={{ color: RED, fontSize: 12 }}>{c.flag}</div>}</td>
                  <td style={td}><span style={{ color: c.percent === 100 ? GREEN : AMBER }}>{c.percent}%</span> ({c.complete} of {c.applicable})</td>
                  <td style={td}>{r.date || "Not set"}</td>
                </>}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14, marginBottom: 14 };
const checkRow = (T) => ({ display: "flex", alignItems: "center", gap: 8, fontFamily: FONT_UI, fontSize: 13, color: T.textBody });

function AwardPicker({ input, awards, value, onPick }) {
  return (
    <select style={input} value={value || ""} onChange={(e) => onPick(awards.find((a) => a.id === e.target.value) || null)}>
      <option value="">None or not set up</option>
      {awards.map((a) => <option key={a.id} value={a.id}>{awardName(a)}</option>)}
    </select>
  );
}

function ItemEditor({ T, label, input, awards, r, setR, isNew }) {
  const set = (k, v) => setR({ ...r, [k]: v });
  const setDisposal = (k, v) => setR({ ...r, disposal: { ...r.disposal, [k]: v } });
  const field = (lbl, el) => <label style={{ display: "block" }}><span style={label}>{lbl}</span>{el}</label>;
  const text = (k, lbl, extra = {}) => field(lbl, <input style={input} value={r[k] ?? ""} onChange={(e) => set(k, e.target.value)} {...extra} />);
  const c = r.check || {};
  return (
    <>
      <h2 style={{ fontFamily: FONT_H, fontSize: 18, color: T.text, margin: "0 0 16px" }}>{isNew ? "New item" : r.description}</h2>
      <div style={{ marginBottom: 14 }}>{text("description", "Description")}</div>
      <div style={grid}>
        {text("idNumber", "Serial or other identification number")}
        {field("Award", <AwardPicker input={input} awards={awards} value={r.awardId} onPick={(a) => setR({ ...r, awardId: a?.id || "", fain: a ? a.fain || a.awardNumber || r.fain : r.fain, fundingSource: r.fundingSource || a?.funderName || "" })} />)}
        {text("fundingSource", "Source of funding")}
        {text("fain", "FAIN")}
      </div>
      <div style={grid}>
        {text("titleHolder", "Title holder")}
        {text("acquisitionDate", "Acquisition date", { type: "date" })}
        {text("cost", "Cost per unit ($)")}
        {text("federalPct", "Federal share of the cost (%)")}
      </div>
      <div style={grid}>
        {text("location", "Location")}
        {text("use", "Use")}
        {text("condition", "Condition")}
        <label style={checkRow(T)}><input type="checkbox" checked={r.usefulLifeOverOneYear !== false} onChange={(e) => set("usefulLifeOverOneYear", e.target.checked)} /> Useful life of more than one year</label>
      </div>
      <h3 style={{ fontFamily: FONT_UI, fontSize: 14, color: T.text, margin: "20px 0 10px" }}>Physical inventory</h3>
      <div style={grid}>
        {text("lastInventory", "Date of the last physical inventory", { type: "date" })}
        <div style={{ ...checkRow(T), color: c.inventoryOverdue ? RED : T.textMuted }}>
          {c.nextInventory ? `Next due ${c.nextInventory}${c.inventoryOverdue ? " (overdue)" : ""}` : "Due dates are worked out when you save."}
        </div>
      </div>
      <h3 style={{ fontFamily: FONT_UI, fontSize: 14, color: T.text, margin: "20px 0 10px" }}>Disposition</h3>
      <div style={grid}>
        {field("Date of disposal", <input style={input} type="date" value={r.disposal?.date || ""} onChange={(e) => setDisposal("date", e.target.value)} />)}
        {field("Sale price ($)", <input style={input} value={r.disposal?.salePrice ?? ""} onChange={(e) => setDisposal("salePrice", e.target.value)} />)}
        {field("Current fair market value per unit ($)", <input style={input} value={r.disposal?.fairMarketValue ?? ""} onChange={(e) => setDisposal("fairMarketValue", e.target.value)} />)}
      </div>
      {c.dispositionFlag && <p style={{ fontFamily: FONT_UI, fontSize: 13, color: AMBER, margin: 0 }}>{c.dispositionNote} Federal share: {usd(c.federalShare)}.</p>}
    </>
  );
}

function BuyEditor({ T, label, input, awards, setError, r, setR, isNew }) {
  const [chk, setChk] = useState(r.check || null);
  const set = (k, v) => setR({ ...r, [k]: v });
  const setItem = (key, patch) => setR({ ...r, checklist: { ...r.checklist, [key]: { ...(r.checklist?.[key] || {}), ...patch } } });
  const field = (lbl, el) => <label style={{ display: "block" }}><span style={label}>{lbl}</span>{el}</label>;
  const text = (k, lbl, extra = {}) => field(lbl, <input style={input} value={r[k] ?? ""} onChange={(e) => set(k, e.target.value)} {...extra} />);

  // The checklist depends on the method and amount, so it is asked for again
  // whenever either changes (or an item is ticked, for the percentage).
  useEffect(() => {
    const t = setTimeout(() => {
      apiFetch(`${API}/procurements/check`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(r) })
        .then((res) => res.json()).then((d) => setChk(d.check)).catch((e) => setError(e.message));
    }, 300);
    return () => clearTimeout(t);
  }, [r, setError]);

  return (
    <>
      <h2 style={{ fontFamily: FONT_H, fontSize: 18, color: T.text, margin: "0 0 16px" }}>{isNew ? "New purchase" : r.item}</h2>
      <div style={{ marginBottom: 14 }}>{text("item", "What was bought")}</div>
      <div style={grid}>
        {text("vendor", "Vendor")}
        {text("amount", "Amount ($)")}
        {text("date", "Date", { type: "date" })}
        {field("Award charged", <AwardPicker input={input} awards={awards} value={r.awardId} onPick={(a) => setR({ ...r, awardId: a?.id || "", awardLabel: a ? awardName(a) : "" })} />)}
      </div>
      <div style={grid}>
        {field("Method used", (
          <select style={input} value={r.method} onChange={(e) => set("method", e.target.value)}>
            <option value="">Choose…</option>
            {METHODS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        ))}
        <div style={{ ...checkRow(T), color: T.textMuted }}>{chk ? `Expected from the amount: ${chk.expected}` : ""}</div>
      </div>
      {chk?.flag && <p style={{ fontFamily: FONT_UI, fontSize: 13, color: RED, margin: "0 0 10px" }}>{chk.flag}</p>}

      <h3 style={{ fontFamily: FONT_UI, fontSize: 14, color: T.text, margin: "20px 0 10px" }}>
        Procurement file {chk && <span style={{ color: chk.percent === 100 ? GREEN : AMBER, fontWeight: 400 }}>{chk.percent}% complete ({chk.complete} of {chk.applicable})</span>}
      </h3>
      {(chk?.items || []).map((i) => {
        const cur = r.checklist?.[i.key] || {};
        return (
          <div key={i.key} style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: 12, marginBottom: 10 }}>
            <div style={{ fontFamily: FONT_UI, fontSize: 13, color: T.textBody, marginBottom: 8 }}>{i.label} <span style={{ color: T.textMuted }}>({i.cite})</span></div>
            <div style={grid}>
              <select style={input} aria-label={`Status: ${i.label}`} value={cur.state || ""} onChange={(e) => setItem(i.key, { state: e.target.value })}>
                <option value="">Missing</option><option value="done">Done</option><option value="na">Not applicable</option>
              </select>
              <input style={input} aria-label={`Evidence name: ${i.label}`} placeholder="Evidence file name" value={cur.evidence?.name || ""} onChange={(e) => setItem(i.key, { evidence: { ...cur.evidence, name: e.target.value } })} />
              <input style={input} aria-label={`Evidence link: ${i.label}`} type="url" placeholder="https://" value={cur.evidence?.url || ""} onChange={(e) => setItem(i.key, { evidence: { ...cur.evidence, url: e.target.value } })} />
            </div>
          </div>
        );
      })}
      {field("Notes", <textarea style={{ ...input, minHeight: 70, resize: "vertical" }} value={r.note || ""} onChange={(e) => set("note", e.target.value)} />)}
    </>
  );
}
